/**
 * Endpoints de **Logística → Ventas Market** (`/api/logistica/ventas-market`).
 *
 * El listado va paginado en el servidor, con los mismos filtros del panel
 * anterior (fechas, número y cliente) más el buscador libre. La edición de los
 * métodos de pago se guarda con `PUT /:id/pagos`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  PagoEditable,
  PagoMarket,
  VentasMarketCatalogos,
  VentasMarketFiltros,
  VentasMarketRespuesta,
} from './ventas-market.types';

export const ventasMarketKeys = {
  lista: (filtros: VentasMarketFiltros) => ['logistica', 'ventas-market', filtros] as const,
  catalogos: () => ['logistica', 'ventas-market', 'catalogos'] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: VentasMarketFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.numero) parametros.set('numero', filtros.numero);
  if (filtros.cliente) parametros.set('cliente', filtros.cliente);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const ventasMarketApi = {
  listar: (
    filtros: VentasMarketFiltros = {},
    signal?: AbortSignal,
  ): Promise<VentasMarketRespuesta> =>
    apiClient.get<VentasMarketRespuesta>(`/api/logistica/ventas-market${cadenaDe(filtros)}`, {
      signal,
    }),

  catalogos: (signal?: AbortSignal): Promise<VentasMarketCatalogos> =>
    apiClient.get<VentasMarketCatalogos>('/api/logistica/ventas-market/catalogos', { signal }),

  editarPagos: (
    id: number,
    pagos: PagoEditable[],
  ): Promise<{ message: string; pagos: PagoMarket[] }> =>
    apiClient.put<{ message: string; pagos: PagoMarket[] }>(
      `/api/logistica/ventas-market/${id}/pagos`,
      { body: { pagos } },
    ),
};

/** Ventas del market del filtro pedido. */
export function useVentasMarket(filtros: VentasMarketFiltros = {}) {
  return useQuery({
    queryKey: ventasMarketKeys.lista(filtros),
    queryFn: ({ signal }) => ventasMarketApi.listar(filtros, signal),
  });
}

/** Métodos de pago del catálogo. */
export function useMetodosPago() {
  return useQuery({
    queryKey: ventasMarketKeys.catalogos(),
    queryFn: ({ signal }) => ventasMarketApi.catalogos(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/** Guarda los métodos de pago de un documento y refresca el listado. */
export function useEditarPagos() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, pagos }: { id: number; pagos: PagoEditable[] }) =>
      ventasMarketApi.editarPagos(id, pagos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'ventas-market'] });
    },
  });
}
